import { currentApyAndInterestRate, formatDollars } from '@fruitful/fruitils';
import React from 'react';
import {
    AccountAllocationCard,
    BodyLargeRegular,
    FontFamilyName,
    H5Regular,
    LabelRegular,
    Shadows,
    toSuperscript,
    XStack,
    YStack,
} from 'smoothie';

import { useConversationalMMContext } from '../context/ConversationalMMContextBase';
import { BudgetGraphSimulator } from './BudgetGraphSimulator';
import {
    BankingDisclosures,
    Header,
    LinearGradientBackgroundCard,
} from './shared';
import { SlideNextButton } from './SlideNextButton';

const EXAMPLE_SPEND_CENTS = 100000;

const SpendSlide = () => {
    const { results } = useConversationalMMContext();
    const { spendTotalCents } = results.moneyMap;

    const isZeroSpend = spendTotalCents === 0;
    const displaySpendCents = isZeroSpend
        ? EXAMPLE_SPEND_CENTS
        : spendTotalCents;

    const SIMULATOR_DAY = 15;
    const spendFormatted = formatDollars(displaySpendCents / 100, false);
    const currentSpendCents = Math.round(
        displaySpendCents * (SIMULATOR_DAY / 30) * 0.55,
    );
    const currentSpendFormatted = formatDollars(currentSpendCents / 100);

    const { formattedApy } = currentApyAndInterestRate();

    return (
        <YStack
            width="100%"
            justifyContent="center"
            alignItems="center"
            gap={24}
        >
            <LinearGradientBackgroundCard>
                <YStack
                    gap={24}
                    width="100%"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Header
                        title="Spend"
                        description="One number for everyday life."
                    />

                    <BodyLargeRegular
                        style={{
                            fontFamily: FontFamilyName.SourceSerifRegular,
                        }}
                    >
                        {isZeroSpend
                            ? `You'll meet with your Guide to determine a Spend amount that works for you.\n\nFor example, each month, ${spendFormatted} would flow into your Spend account for day-to-day spending — groceries, gas, going out. No budgeting, no math: stay within this number and everything else runs in the background.`
                            : `Each month, ${spendFormatted} flows into your Spend account for day-to-day spending — groceries, gas, going out. No budgeting, no math: stay within this number and everything else runs in the background.`}
                    </BodyLargeRegular>

                    <AccountAllocationCard
                        label="Spend"
                        labelColor="$leaf"
                        accountName="Spend"
                        accountSubtitle="Fruitful Cash Account"
                        amountText={`${spendFormatted}/mo`}
                        icon="fruitful"
                        iconColor="green"
                    />

                    <BodyLargeRegular
                        style={{
                            fontFamily: FontFamilyName.SourceSerifRegular,
                        }}
                    >
                        Your Spend account is separate from your bills, so you
                        never have to guess what&apos;s safe to spend.
                    </BodyLargeRegular>

                    <YStack
                        alignSelf="center"
                        width="100%"
                        padding={16}
                        backgroundColor="rgba(255, 255, 255, 0.5)"
                        borderRadius={16}
                        borderWidth={1}
                        borderColor="$white"
                        gap={8}
                        {...Shadows.SMALL}
                    >
                        <XStack
                            width="100%"
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            <YStack gap={4}>
                                <LabelRegular color="$gray1000">
                                    Current spend this month
                                </LabelRegular>
                                <H5Regular>{currentSpendFormatted}</H5Regular>
                            </YStack>
                            <YStack alignItems="flex-end" gap={4}>
                                <LabelRegular color="$gray1000">
                                    Budget
                                </LabelRegular>
                                <H5Regular>{spendFormatted}</H5Regular>
                            </YStack>
                        </XStack>
                        <BudgetGraphSimulator
                            budgetCents={displaySpendCents}
                            dayOfMonth={SIMULATOR_DAY}
                            height={100}
                        />
                    </YStack>

                    <BodyLargeRegular
                        style={{
                            fontFamily: FontFamilyName.SourceSerifRegular,
                        }}
                    >
                        {`Your Spend card - physical and virtual — earns 2% cash back${toSuperscript(2)} on everything, and the account earns ${formattedApy} APY${toSuperscript(1)}.`}
                    </BodyLargeRegular>
                    <BodyLargeRegular
                        style={{
                            fontFamily: FontFamilyName.SourceSerifRegular,
                        }}
                    >
                        Like your Core card, it&apos;s a secured charge card:
                        you can only spend what&apos;s there, no debt, no
                        interest, impossible to overspend.
                    </BodyLargeRegular>
                    <SlideNextButton />
                </YStack>
            </LinearGradientBackgroundCard>
            <BankingDisclosures color="$black" />
        </YStack>
    );
};

export default SpendSlide;
